import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { on } from '@ember/modifier';
import { fn, get } from '@ember/helper';
import { LinkTo } from '@ember/routing';

// Simple helper functions
export const eq = (a: any, b: any) => a === b;
export const add = (a: number, b: number) => a + b;
export const concat = (...args: any[]) => args.join('');

export default class PlayDaysOverview extends Component {
  @tracked selectedDayIndex = 0; // Default to play day 1

  get playDays() {
    if (!this.args.tournament?.matches) {
      return [];
    }

    // Group matches by scheduled date
    const matchesByDate = new Map();
    this.args.tournament.matches.forEach(match => {
      if (match.scheduledDate) {
        const dateKey = new Date(match.scheduledDate).toDateString();
        if (!matchesByDate.has(dateKey)) {
          matchesByDate.set(dateKey, []);
        }
        matchesByDate.get(dateKey).push(match);
      }
    });

    // Convert to array and sort by date
    const playDays = Array.from(matchesByDate.entries())
      .sort(([dateA], [dateB]) => new Date(dateA) - new Date(dateB))
      .map(([dateKey, matches], index) => {
        return {
          index: index,
          date: dateKey,
          matches: matches.sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate)),
          standings: this.calculateStandingsUpToDate(dateKey)
        };
      });

    return playDays;
  }

  get selectedPlayDay() {
    return this.playDays[this.selectedDayIndex] || this.playDays[0];
  }

  calculateStandingsUpToDate(targetDate) {
    if (!this.args.tournament?.matches || !this.args.tournament?.teams) {
      return [];
    }

    const targetDateTime = new Date(targetDate).getTime();
    
    // Get all completed matches up to and including target date
    const completedMatches = this.args.tournament.matches.filter(match => {
      return match.isFinal && 
             match.scheduledDate && 
             new Date(match.scheduledDate).getTime() <= targetDateTime;
    });

    // Calculate wins/losses for each team
    const teamStats = new Map();
    
    // Initialize team stats
    this.args.tournament.teams.forEach(team => {
      teamStats.set(team.id, {
        team: team,
        wins: 0,
        losses: 0
      });
    });

    // Count wins and losses from completed matches
    completedMatches.forEach(match => {
      if (match.victor && match.teams?.length === 2) {
        const winnerId = match.victor.id;
        const loserId = match.teams.find(team => team.id !== winnerId)?.id;
        
        if (teamStats.has(winnerId)) {
          teamStats.get(winnerId).wins++;
        }
        if (loserId && teamStats.has(loserId)) {
          teamStats.get(loserId).losses++;
        }
      }
    });

    // Sort by wins (desc), then losses (asc)
    return Array.from(teamStats.values()).sort((a, b) => {
      if (b.wins !== a.wins) {
        return b.wins - a.wins;
      }
      return a.losses - b.losses;
    });
  }

  getMatchScore(match) {
    if (!match?.teams?.length || !match?.victor || !match?.bestOf) {
      return 'TBD';
    }

    // If we have rounds data with victor info, calculate actual score
    const hasUsableRounds = match.rounds && match.rounds.length > 0 && 
                           match.rounds.some(round => round?.victor?.slug);
    
    if (hasUsableRounds) {
      let team0Score = 0;
      let team1Score = 0;

      match.rounds.forEach((round) => {
        if (round?.victor?.slug) {
          if (round.victor.slug === match.teams[0]?.slug) {
            team0Score++;
          } else if (round.victor.slug === match.teams[1]?.slug) {
            team1Score++;
          }
        }
      });

      return `${team0Score}-${team1Score}`;
    }

    // Fallback: Use victor and bestOf to show winner format
    // This assumes the victor won the minimum required rounds
    const isTeam0Winner = match.victor.id === match.teams[0]?.id;
    const winningScore = Math.ceil(match.bestOf / 2); // e.g., 2 wins needed for Bo3
    const losingScore = winningScore - 1; // Minimum possible losing score
    
    return isTeam0Winner 
      ? `${winningScore}-${losingScore}` 
      : `${losingScore}-${winningScore}`;
  }

  @action
  selectPlayDay(index) {
    this.selectedDayIndex = index;
  }

  <template>
    <div class='widget-item'>
      <div class='section-title-wrap violet small-space'>
        <h2 class='section-title medium'>Play Days Overview</h2>
        <div class='section-title-separator'></div>
      </div>
      <div class='tab-wrap'>
        <div class='tab-header items-{{this.playDays.length}}'>
          {{#each this.playDays as |playDay|}}
            <div class='tab-header-item {{if (eq playDay.index this.selectedDayIndex) "selected"}}' {{on 'click' (fn this.selectPlayDay playDay.index)}}>
              <p class='tab-header-item-text'>Play Day {{add playDay.index 1}}</p>
            </div>
          {{/each}}
        </div>
      <div class='tab-body'>
        <div class='tab-item'>
          {{#if this.selectedPlayDay}}
            <div class='grid1col-2col centered'>
              <div class='table standings'>
                <div class='table-row-header'>
                  <div class='table-row-header-item position'>
                    <p class='table-row-header-title'>Standings</p>
                  </div>
                  <div class='table-row-header-item padded'>
                    <p class='table-row-header-title'>W</p>
                  </div>
                  <div class='table-row-header-item padded'>
                    <p class='table-row-header-title'>L</p>
                  </div>
                </div>
                <div class='table-rows'>
                  {{#each this.selectedPlayDay.standings as |teamStat index|}}
                    <div class='table-row'>
                      <div class='table-row-item position'>
                        <p class='table-text'>{{add index 1}}</p>
                        <div class='team-info-wrap'>
                          <img class='team-logo small' src='{{teamStat.team.logo.url}}' alt='logo-{{teamStat.team.slug}}' />
                          <div class='team-info'>
                            <p class='team-name'>{{teamStat.team.name}}</p>
                            <p class='team-country'>{{teamStat.team.country}}</p>
                          </div>
                        </div>
                      </div>
                      <div class='table-row-item'>
                        <p class='table-text bold'>{{teamStat.wins}}</p>
                      </div>
                      <div class='table-row-item'>
                        <p class='table-text bold'>{{teamStat.losses}}</p>
                      </div>
                    </div>
                  {{/each}}
                </div>
              </div>
              <div class='table matches hoverable'>
                <div class='table-row-header'>
                  <div class='table-row-header-item left'>
                    <p class='table-row-header-title'>Results</p>
                  </div>
                  <div class='table-row-header-item'></div>
                  <div class='table-row-header-item'></div>
                  <div class='table-row-header-item'>
                    <p class='table-row-header-title'>Video</p>
                  </div>
                </div>
                <div class='table-rows'>
                  {{#each this.selectedPlayDay.matches as |match|}}
                    {{#if match.isFinal}}
                      <div class='table-row'>
                        <div class='table-row-item'>
                          <div class='team-info-wrap'>
                            <img class='team-logo small' src='{{get match.teams "0.logo.url"}}' alt='logo-{{get match.teams "0.slug"}}' />
                            <div class='team-info'>
                              <p class='team-name'>{{get match.teams "0.name"}}</p>
                              <p class='team-country'>{{get match.teams "0.country"}}</p>
                            </div>
                          </div>
                        </div>
                        <div class='table-row-item'>
                          <div class='match-stats'>
                            <p class='match-score small'>{{this.getMatchScore match}}</p>
                            <a href='/esports/matches/{{match.slug}}' class='match-link'>Full Stats</a>
                          </div>
                        </div>
                        <div class='table-row-item'>
                          <div class='team-info-wrap inverted'>
                            <img class='team-logo small' src='{{get match.teams "1.logo.url"}}' alt='logo-{{get match.teams "1.slug"}}' />
                            <div class='team-info'>
                              <p class='team-name'>{{get match.teams "1.name"}}</p>
                              <p class='team-country'>{{get match.teams "1.country"}}</p>
                            </div>
                          </div>
                        </div>
                        <div class='table-row-item'>
                          <LinkTo @route='esports.matches.match' @model={{match.slug}} class='table-text-link'>
                            <svg class='play-button-icon tiny'>
                              <use href='#svg-play'></use>
                            </svg>
                            <span class='link-text'>Watch replay</span>
                          </LinkTo>
                        </div>
                      </div>
                    {{/if}}
                  {{/each}}
                </div>
              </div>
            </div>
          {{else}}
            <p>No play day data available</p>
          {{/if}}
        </div>
      </div>
    </div>
  </div>
  </template>
}
